import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  Play,
  Plus,
  Filter,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  XCircle,
  List,
  CalendarDays,
  Clock3,
  Trophy
} from 'lucide-react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addHours, addDays, startOfDay, subDays } from 'date-fns';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import FeaturedLayout from './layouts/FeaturedLayout';
import {
  Button,
  Badge,
  Card,
  CardContent
} from './ui';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { mundoDelJuego, MUNDO_RED } from '../lib/mundoTorneo';
import { usePermissions } from '../hooks/usePermissions';
import { getStaffGroups, getAllGroupsWithStaff, getJuegosByGrupo, getSesionesByGrupo, getCurrentStaff } from '../lib/supabase';
import { cn } from '../lib/utils';

const locales = { es };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales
});

const CALENDAR_MESSAGES = {
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  allDay: 'Todo el día',
  week: 'Semana',
  work_week: 'Semana laboral',
  day: 'Día',
  month: 'Mes',
  previous: 'Anterior',
  next: 'Siguiente',
  yesterday: 'Ayer',
  tomorrow: 'Mañana',
  today: 'Hoy',
  agenda: 'Agenda',
  noEventsInRange: 'No hay eventos en este periodo'
};

const STATUS_COLORS = {
  programado: '#2563eb',
  terminado: '#1f2937',
  'en curso': '#059669',
  cancelado: '#b91c1c'
};

const calendarFormats = {
  eventTimeRangeFormat: () => '',
  eventTimeRangeStartFormat: () => '',
  eventTimeRangeEndFormat: () => ''
};

const CalendarOverview = ({
  embedded = false,
  title: titleOverride = 'Agenda',
  subtitle: subtitleOverride = 'Visualiza entrenamientos, eventos y pendientes en una sola vista',
  overrideGroups = null,
  initialGroupIds = null,
  showQuickActions = true,
  defaultDisplayMode = 'calendar',
  allowedTypes = null,
  hideRol = false,
  hideTypeFilter = false,
  extraFilters = null,
  extraFilterCount = 0
}) => {
  const { coach } = useAuth();
  const { org } = useOrganization();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [displayMode, setDisplayMode] = useState(defaultDisplayMode);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFetchingEvents, setIsFetchingEvents] = useState(false);
  const hasInitializedGroups = useRef(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const typesLocked = Array.isArray(allowedTypes) && allowedTypes.length === 1;
  const filterCount = useMemo(
    () =>
      getFilterCount({
        groups,
        selectedGroupIds,
        availableTypes,
        selectedTypes,
        showCancelled,
        typesLocked,
        showTypes: !hideTypeFilter,
        extraFilterCount
      }),
    [groups, selectedGroupIds, availableTypes, selectedTypes, showCancelled, typesLocked, hideTypeFilter, extraFilterCount]
  );

  const loadGroups = useCallback(async () => {
    if (!coach && !isAdmin) return;
    try {
      let data = null;
      let error = null;

      if (isAdmin) {
        ({ data, error } = await getAllGroupsWithStaff());
      } else {
        let staffId = coach?.id || null;
        if (!staffId) {
          const { data: staffData, error: staffError } = await getCurrentStaff();
          if (staffError) throw staffError;
          staffId = staffData?.id || null;
        }
        if (!staffId) {
          setGroups([]);
          return;
        }
        ({ data, error } = await getStaffGroups(staffId));
      }

      if (error) throw error;

      const formatted = isAdmin ? data : (data || []).map((item) => item.grupos);
      setGroups(formatted);
    } catch (error) {
      console.error('Error loading groups for calendar:', error);
    } finally {
      setLoading(false);
    }
  }, [coach, isAdmin]);

  useEffect(() => {
    hasInitializedGroups.current = false;
    if (overrideGroups && Array.isArray(overrideGroups)) {
      setGroups(overrideGroups);
      setLoading(false);
      return;
    }
    loadGroups();
  }, [overrideGroups, loadGroups]);

  useEffect(() => {
    const updateIsMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setView('day');
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    if (!hasInitializedGroups.current && groups.length) {
      const presetIds = Array.isArray(initialGroupIds)
        ? initialGroupIds
        : Array.isArray(location.state?.groupIds)
          ? location.state.groupIds
          : null;

      const validPreset = presetIds
        ? groups
            .filter((group) => presetIds.includes(group.id))
            .map((group) => group.id)
        : [];

      const initialSelection =
        validPreset.length > 0 ? validPreset : groups.map((group) => group.id);

      setSelectedGroupIds(initialSelection);
      hasInitializedGroups.current = true;
    }
  }, [groups, location.state, initialGroupIds]);

  const combineDateTime = (dateStr, timeStr) => {
    if (!dateStr) return new Date();
    const safeTime = timeStr && timeStr.length >= 4 ? timeStr : '08:00';
    return new Date(`${dateStr}T${safeTime.length === 5 ? `${safeTime}:00` : safeTime}`);
  };

  const normalizeEvent = (event, fallbackGroup) => {
    const groupId =
      event.grupo_id || event.group_uuid || event.group_id || event.groupId || fallbackGroup?.id;
    const groupName =
      event.groupName ||
      event.grupos?.nombre_del_grupo ||
      event.nombre_del_grupo ||
      fallbackGroup?.nombre_del_grupo;
    const startTime =
      event.hora ||
      event.hora_inicio ||
      event.start_time ||
      event.startTime ||
      (event.serie_entrenamiento && event.serie_entrenamiento.hora_inicio);
    const endTime =
      event.hora_fin ||
      event.hora_termino ||
      event.end_time ||
      event.endTime ||
      (event.serie_entrenamiento && event.serie_entrenamiento.hora_fin);

    const hasExplicitStart = Boolean(startTime);
    const hasExplicitEnd = Boolean(endTime);

    const start = hasExplicitStart
      ? combineDateTime(event.fecha, startTime)
      : new Date(`${event.fecha}T00:00:00`);
    const derivedDuration = Number(event.duracion || event.duration || 2);
    const end = hasExplicitEnd
      ? combineDateTime(event.fecha, endTime)
      : hasExplicitStart
        ? addHours(start, derivedDuration)
        : addDays(start, 1);

    const rawStatus = (event.estatus || 'Programado').toLowerCase();
    const tipo = (event.tipo_evento || 'evento').toLowerCase();
    const isTraining = tipo.includes('entrenamiento');
    const now = new Date();
    const isPast = end < now;
    const pendingAttendance = isTraining && isPast && rawStatus !== 'terminado';

    return {
      ...event,
      title: `${event.nombre} · ${groupName || 'Sin grupo'}`,
      start,
      end,
      allDay: !hasExplicitStart,
      resource: {
        grupoId: groupId,
        eventoId: event.id || event.evento_id || event.eventId,
        groupName,
        tipo,
        status: rawStatus,
        pendingAttendance
      },
      originalEvent: event
    };
  };

  const normalizeJuegoEvent = (juego, fallbackGroup) => {
    const groupId = juego.grupo_id || fallbackGroup?.id;
    const groupName =
      fallbackGroup?.nombre_del_grupo ||
      juego.grupo?.nombre_del_grupo ||
      juego.grupos?.nombre_del_grupo ||
      'Sin grupo';
    const opponent = juego.equipo?.nombre || juego.equipo_nombre || 'Equipo por definir';
    const start = combineDateTime(juego.fecha, juego.hora);
    const duration = Number(juego.duracion || 2);
    const end = juego.hora_fin
      ? combineDateTime(juego.fecha, juego.hora_fin)
      : addHours(start, Number.isNaN(duration) ? 2 : duration);
    const normalizedStatus = (juego.estatus || 'programado').replace('_', ' ');
    const now = new Date();
    const pendingAttendance = end < now && normalizedStatus !== 'terminado' && normalizedStatus !== 'cancelado';
    const campoNumero = juego.campo_numero ?? null;
    const canchaLabel = juego.cancha || juego.campo || juego.ubicacion || 'Cancha principal';
    const campoLabel = campoNumero
      ? `${canchaLabel} · Campo ${campoNumero}`
      : canchaLabel;

    return {
      ...juego,
      title: `${groupName} vs ${opponent}`,
      start,
      end,
      allDay: !juego.hora,
      resource: {
        grupoId: groupId,
        eventoId: juego.id,
        juegoId: juego.id,
        groupName,
        tipo: 'juego',
        // De que mundo es: el calendario muestra los dos, cada uno etiquetado.
        mundo: mundoDelJuego(juego, org?.id),
        status: normalizedStatus,
        pendingAttendance,
        torneoId: juego.torneo_id,
        campoNumero,
        canchaLabel,
        campoLabel
      },
      originalEvent: juego
    };
  };

  const normalizeSesionEvent = (sesion, fallbackGroup) => {
    const groupId = sesion.grupo_id || fallbackGroup?.id;
    const groupName =
      fallbackGroup?.nombre_del_grupo ||
      sesion.grupos?.nombre_del_grupo ||
      'Sin grupo';
    const title = sesion.titulo || sesion.serie_entrenamiento?.nombre || 'Entrenamiento';
    const start = combineDateTime(sesion.fecha, sesion.hora_inicio);
    const duration = Number(sesion.duracion || 2);
    const end = sesion.hora_fin
      ? combineDateTime(sesion.fecha, sesion.hora_fin)
      : addHours(start, Number.isNaN(duration) ? 2 : duration);
    const normalizedStatus = (sesion.estatus || 'programado').replace('_', ' ');
    const now = new Date();
    const pendingAttendance = end < now && normalizedStatus !== 'terminado' && normalizedStatus !== 'cancelado';

    return {
      ...sesion,
      title,
      start,
      end,
      allDay: !sesion.hora_inicio,
      resource: {
        grupoId: groupId,
        eventoId: sesion.id,
        sesionId: sesion.id,
        groupName,
        tipo: 'entrenamiento',
        status: normalizedStatus,
        pendingAttendance,
        serieId: sesion.serie_entrenamiento_id
      },
      originalEvent: sesion
    };
  };

  const loadCalendarEvents = useCallback(async () => {
    if (!selectedGroupIds.length) {
      setCalendarEvents([]);
      setAvailableTypes([]);
      return;
    }

    try {
      setIsFetchingEvents(true);
      const rangeStart = startOfDay(subDays(currentDate, 30));
      const rangeEnd = addDays(startOfDay(currentDate), 30);
      const startDate = format(rangeStart, 'yyyy-MM-dd');
      const endDate = format(rangeEnd, 'yyyy-MM-dd');
      const results = await Promise.all(
        selectedGroupIds.map(async (groupId) => {
          const groupInfo = groups.find((g) => g.id === groupId);
          const typesAllowed = Array.isArray(allowedTypes) ? allowedTypes : null;
          const shouldLoadJuegos = !typesAllowed || typesAllowed.includes('juego');
          const shouldLoadSesiones = !typesAllowed || typesAllowed.includes('entrenamiento');

          const juegosData = shouldLoadJuegos ? await getJuegosByGrupo(groupId) : [];
          const { data: sesionesData } = shouldLoadSesiones
            ? await getSesionesByGrupo({
                groupId,
                startDate,
                endDate,
                tipo: 'entrenamiento'
              })
            : { data: [] };

          const eventosNormalizados = (sesionesData || []).map((sesion) =>
            normalizeSesionEvent(sesion, groupInfo)
          );
          const juegosNormalizados = (juegosData || []).map((juego) =>
            normalizeJuegoEvent(juego, groupInfo)
          );

          return [...eventosNormalizados, ...juegosNormalizados];
        })
      );

      const flattened = results.flat();
      setCalendarEvents(flattened);
      const types = Array.from(
        new Set(
          flattened.map((event) => event.resource?.tipo || 'evento')
        )
      );
      const filteredTypes = Array.isArray(allowedTypes)
        ? types.filter((type) => allowedTypes.includes(type))
        : types;
      setAvailableTypes(filteredTypes);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setIsFetchingEvents(false);
    }
  }, [groups, selectedGroupIds, currentDate, getSesionesByGrupo, allowedTypes]);

  useEffect(() => {
    if (selectedGroupIds.length) {
      loadCalendarEvents();
    }
  }, [selectedGroupIds, loadCalendarEvents]);

  useEffect(() => {
    if (availableTypes.length === 0) {
      setSelectedTypes([]);
      return;
    }
    setSelectedTypes((prev) => {
      if (Array.isArray(allowedTypes) && allowedTypes.length) {
        const filteredAllowed = allowedTypes.filter((type) => availableTypes.includes(type));
        return filteredAllowed.length ? filteredAllowed : availableTypes;
      }
      if (!prev.length) return availableTypes;
      const filtered = prev.filter((type) => availableTypes.includes(type));
      return filtered.length ? filtered : availableTypes;
    });
  }, [availableTypes]);

  useEffect(() => {
    if (hideRol && displayMode === 'rol') {
      setDisplayMode('calendar');
    }
  }, [hideRol, displayMode]);

  const filteredEvents = useMemo(() => {
    return calendarEvents.filter((event) => {
      const status = event.resource?.status || 'programado';
      const type = event.resource?.tipo || 'evento';
      if (!showCancelled && status === 'cancelado') return false;
      if (selectedTypes.length && !selectedTypes.includes(type)) return false;
      return true;
    });
  }, [calendarEvents, showCancelled, selectedTypes]);

  const timeWindow = useMemo(() => {
    if (!filteredEvents.length) {
      const base = startOfDay(new Date());
      return {
        min: addHours(base, 6),
        max: addHours(base, 22),
        scroll: addHours(base, 15)
      };
    }
    let earliest = filteredEvents[0].start;
    let latest = filteredEvents[0].end;
    filteredEvents.forEach((event) => {
      if (event.start < earliest) earliest = event.start;
      if (event.end > latest) latest = event.end;
    });
    return {
      min: addHours(earliest, -1),
      max: addHours(latest, 1),
      scroll: earliest
    };
  }, [filteredEvents]);

  const calendarDisplayEvents = useMemo(() => {
    const clusters = new Map();
    filteredEvents.forEach((event) => {
      const bucket = new Date(event.start);
      bucket.setMinutes(0, 0, 0);
      const key = `${bucket.toISOString()}-${event.resource?.tipo || 'evento'}`;
      if (!clusters.has(key)) clusters.set(key, []);
      clusters.get(key).push(event);
    });

    const clusterMeta = new Map();

    clusters.forEach((events) => {
      events
        .sort((a, b) => a.start - b.start || (a.title || '').localeCompare(b.title || ''))
        .forEach((event, index) => {
          clusterMeta.set(event, {
            index: index + 1,
            total: events.length
          });
        });
    });

    return filteredEvents.map((event) => ({
      ...event,
      resource: {
        ...event.resource,
        clusterIndex: clusterMeta.get(event)?.index || 1,
        clusterTotal: clusterMeta.get(event)?.total || 1
      }
    }));
  }, [filteredEvents]);

  const statusSummary = useMemo(() => {
    const summary = {
      programado: 0,
      'en curso': 0,
      terminado: 0,
      pendientes: 0
    };

    filteredEvents.forEach((event) => {
      const status = event.resource?.status || 'programado';
      if (summary[status] !== undefined) {
        summary[status] += 1;
      }
      if (event.resource?.pendingAttendance) {
        summary.pendientes += 1;
      }
    });

    return summary;
  }, [filteredEvents]);

  const groupedGameDays = useMemo(() => {
    const games = filteredEvents.filter((event) => event.resource?.tipo === 'juego');
    const grouped = new Map();

    games.forEach((event) => {
      const dateKey = format(event.start, 'yyyy-MM-dd');
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, {
          dateKey,
          date: event.start,
          torneos: new Map()
        });
      }
      const dayGroup = grouped.get(dateKey);
      const torneoId = event.resource?.torneoId || 'sin_torneo';
      const torneoName =
        event.originalEvent?.torneo?.nombre ||
        event.originalEvent?.torneo_nombre ||
        'Juegos amistosos';
      if (!dayGroup.torneos.has(torneoId)) {
        dayGroup.torneos.set(torneoId, {
          torneoId,
          torneoName,
          campos: new Map()
        });
      }
      const torneoGroup = dayGroup.torneos.get(torneoId);
      const campoNumero = event.resource?.campoNumero ?? null;
      const canchaLabel = event.resource?.canchaLabel || event.resource?.campoLabel || 'Cancha';
      const campoLabel =
        campoNumero !== null ? `${canchaLabel} · Campo ${campoNumero}` : canchaLabel;
      const campoKey =
        campoNumero !== null
          ? `${canchaLabel || 'cancha'}__${campoNumero}`
          : canchaLabel || 'sin_campo';

      if (!torneoGroup.campos.has(campoKey)) {
        torneoGroup.campos.set(campoKey, {
          campoKey,
          campoNumero,
          canchaLabel,
          campoLabel,
          juegos: []
        });
      }
      torneoGroup.campos.get(campoKey).juegos.push(event);
    });

    return Array.from(grouped.values())
      .sort((a, b) => a.date - b.date)
      .map((day) => ({
        ...day,
        label: format(day.date, "EEEE d 'de' MMMM", { locale: es }),
        torneos: Array.from(day.torneos.values()).map((torneo) => ({
          ...torneo,
          campos: Array.from(torneo.campos.values())
            .map((campo) => ({
              ...campo,
              juegos: campo.juegos.sort((a, b) => a.start - b.start)
            }))
            .sort((a, b) => {
              if (a.campoNumero !== null && b.campoNumero !== null) {
                return a.campoNumero - b.campoNumero;
              }
              if (a.campoNumero !== null) return -1;
              if (b.campoNumero !== null) return 1;
              return a.campoLabel.localeCompare(b.campoLabel);
            })
        }))
      }));
  }, [filteredEvents]);

  const totalRolGames = useMemo(() => {
    return groupedGameDays.reduce(
      (total, day) =>
        total +
        day.torneos.reduce(
          (acc, torneo) =>
            acc + torneo.campos.reduce((campoAcc, campo) => campoAcc + campo.juegos.length, 0),
          0
        ),
      0
    );
  }, [groupedGameDays]);

  const agendaRangeStart = useMemo(() => startOfDay(subDays(currentDate, 30)), [currentDate]);
  const agendaRangeEnd = useMemo(() => addDays(startOfDay(currentDate), 30), [currentDate]);

  const agendaEvents = useMemo(() => {
    return filteredEvents
      .filter((event) => event.end >= agendaRangeStart && event.start <= agendaRangeEnd)
      .sort((a, b) => a.start - b.start);
  }, [filteredEvents, agendaRangeStart, agendaRangeEnd]);

  const eventPropGetter = (event) => {
    const status = event.resource?.status || 'programado';
    const pending = event.resource?.pendingAttendance;
    const baseColor = STATUS_COLORS[status] || '#0f172a';
    const backgroundColor = pending ? '#f97316' : baseColor;

    return {
      style: {
        backgroundColor,
        borderRadius: '12px',
        border: pending ? '2px solid #fb923c' : '1px solid rgba(255,255,255,0.25)',
        opacity: 0.96,
        color: '#fff',
        boxShadow: '0 10px 20px rgba(15,23,42,0.25)',
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '4px'
      }
    };
  };

  const navigateToEvent = (event) => {
    setPreviewEvent(null);
    const tipo = event.resource?.tipo || '';
    const grupoId = event.resource?.grupoId;
    const eventoId = event.resource?.eventoId;
    const juegoId = event.resource?.juegoId;
    const groupData = groups.find((g) => g.id === grupoId);
    const rawEvent = event.originalEvent;

    if (!grupoId || !eventoId) {
      console.warn('Evento sin identificadores suficientes', event);
      return;
    }

    if (tipo === 'juego' && juegoId) {
      navigate(`/torneos/juegos/${juegoId}`, {
        state: {
          fromCalendar: true,
          group: groupData,
          juego: rawEvent
        }
      });
      return;
    }

    if (tipo.includes('entrenamiento')) {
      navigate(`/attendance/${grupoId}/${eventoId}`, {
        state: {
          fromCalendar: true,
          group: groupData,
          event: rawEvent,
          mode: 'entrenamiento',
          fecha: rawEvent?.fecha
        }
      });
    } else {
      navigate('/torneos/juegos', {
        state: {
          highlightEventId: eventoId,
          selectedGroup: groupData
        }
      });
    }
  };

  const handleViewGroup = (event) => {
    const grupoId = event.resource?.grupoId;
    if (!grupoId) return;
    setPreviewEvent(null);
    navigate(`/groups/${grupoId}`);
  };

  const handleEventClick = (event) => {
    setPreviewEvent(event);
  };

  const handleOpenRolGame = (event) => {
    navigateToEvent(event);
  };

  const handleCalendarNavigate = (action) => {
    if (action === 'TODAY') {
      setCurrentDate(new Date());
      return;
    }

    const direction = action === 'NEXT' ? 1 : action === 'PREV' ? -1 : 0;
    if (direction === 0) return;

    const incrementDays = view === 'day' ? 1 : view === 'week' ? 7 : 30;
    setCurrentDate((prev) => addDays(prev, direction * incrementDays));
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
  };

  const handleStartTraining = () => {
    if (!selectedGroupIds.length) return;
    navigate(`/attendance/${selectedGroupIds[0]}`);
  };

  const handleCreateEvent = () => {
    const fallbackGroupId =
      selectedGroupIds.length > 0
        ? selectedGroupIds[0]
        : groups.length === 1
          ? groups[0].id
          : null;

    if (fallbackGroupId) {
      const targetGroup = groups.find((group) => group.id === fallbackGroupId) || null;
      navigate(`/groups/${fallbackGroupId}`, {
        state: {
          group: targetGroup,
          showCreateEvent: true
        }
      });
      return;
    }

    navigate('/groups');
  };

  const calendarContent = (
    <div className="p-4 space-y-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              size="sm"
              className="flex items-center gap-2"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <Filter className="h-4 w-4" />
              {filterCount > 0 ? `Filtros (${filterCount})` : 'Filtros'}
            </Button>
            {showQuickActions && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleStartTraining}
                  disabled={!selectedGroupIds.length}
                  className="flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                  Entrenar ahora
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleCreateEvent}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo evento
                </Button>
              </>
            )}
          </div>
        </div>

        {showFilters && (
          <FiltersPanel
            groups={groups}
            selectedGroupIds={selectedGroupIds}
            onGroupChange={setSelectedGroupIds}
            availableTypes={availableTypes}
            selectedTypes={selectedTypes}
            onTypesChange={setSelectedTypes}
            showCancelled={showCancelled}
            onToggleCancelled={() => setShowCancelled((prev) => !prev)}
            typesLocked={typesLocked}
            showTypesFilter={!hideTypeFilter}
            extraFilters={extraFilters}
          />
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
          <SummaryCard
            label="Programados"
            value={statusSummary.programado}
            tone="info"
          />
          <SummaryCard
            label="En curso"
            value={statusSummary['en curso']}
            tone="success"
          />
          <SummaryCard
            label="Terminados"
            value={statusSummary.terminado}
            tone="muted"
          />
          <SummaryCard
            label="Pendientes"
            value={statusSummary.pendientes}
            tone="warning"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border rounded-lg px-3 py-2 bg-muted/40">
          <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pr-1">
            <Button
              size="sm"
              variant={displayMode === 'calendar' ? 'default' : 'ghost'}
              className="flex items-center gap-1 text-xs px-2"
              onClick={() => setDisplayMode('calendar')}
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Calendario</span>
            </Button>
            <Button
              size="sm"
              variant={displayMode === 'agenda' ? 'default' : 'ghost'}
              className="flex items-center gap-1 text-xs px-2"
              onClick={() => setDisplayMode('agenda')}
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Agenda</span>
            </Button>
            {!hideRol && (
              <Button
                size="sm"
                variant={displayMode === 'rol' ? 'default' : 'ghost'}
                className="flex items-center gap-1 text-xs px-2"
                onClick={() => setDisplayMode('rol')}
              >
                <Trophy className="h-4 w-4" />
                <span className="hidden sm:inline">Rol de juegos</span>
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {displayMode === 'rol'
              ? 'Agrupamos los juegos por torneo y fecha para resumir el rol del día.'
              : displayMode === 'agenda'
                ? 'Consulta la lista cronológica de entrenamientos y juegos seleccionados.'
                : 'Visualiza entrenamientos y juegos sobre el calendario.'}
          </p>
        </div>

        {displayMode === 'calendar' ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <div className="flex flex-wrap items-center gap-3">
                <LegendDot color="#2563eb" label="Programado" />
                <LegendDot color="#059669" label="En curso" />
                <LegendDot color="#1f2937" label="Terminado" />
                <LegendDot color="#f97316" label="Pendiente asistencia" />
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <CalendarIcon className="h-3 w-3" />
                  {filteredEvents.length} registros
                </Badge>
                <Badge variant="outline" className="flex items-center gap-1 text-xs">
                  <AlertCircle className="h-3 w-3 text-amber-500" />
                  {statusSummary.pendientes} pendientes
                </Badge>
              </div>
            </div>

            <Card className="overflow-hidden border shadow-none">
              <CardContent className="p-0">
                <CalendarToolbar
                  currentDate={currentDate}
                  view={view}
                  onNavigate={handleCalendarNavigate}
                  onViewChange={handleViewChange}
                  isMobile={isMobile}
                />
                {loading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    Cargando grupos...
                  </div>
                ) : (
                  <BigCalendar
                    culture="es"
                    localizer={localizer}
                    events={calendarDisplayEvents}
                    startAccessor="start"
                    endAccessor="end"
                    defaultView={isMobile ? 'day' : 'week'}
                    views={isMobile ? ['day'] : ['day', 'week']}
                    style={{ height: 'calc(100vh - 320px)' }}
                    dayLayoutAlgorithm="no-overlap"
                    eventPropGetter={eventPropGetter}
                    tooltipAccessor="title"
                    onSelectEvent={handleEventClick}
                    components={{ event: CalendarEventCard }}
                    date={currentDate}
                    view={view}
                    onNavigate={(date) => setCurrentDate(date)}
                    onView={(nextView) => setView(nextView)}
                    min={timeWindow.min}
                    max={timeWindow.max}
                    scrollToTime={timeWindow.scroll}
                    formats={calendarFormats}
                    messages={CALENDAR_MESSAGES}
                  />
                )}
              </CardContent>
            </Card>
          </>
        ) : displayMode === 'agenda' ? (
          <Card className="overflow-hidden border shadow-none">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  Cargando grupos...
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 border-b px-4 py-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                      <CalendarIcon className="h-3 w-3" />
                      {agendaEvents.length} registros
                    </Badge>
                    <Badge variant="outline" className="flex items-center gap-1 text-xs">
                      <AlertCircle className="h-3 w-3 text-amber-500" />
                      {statusSummary.pendientes} pendientes
                    </Badge>
                  </div>
                  <AgendaList
                    events={agendaEvents}
                    rangeStart={agendaRangeStart}
                    rangeEnd={agendaRangeEnd}
                    onSelectEvent={handleEventClick}
                    onOpenGame={navigateToEvent}
                  />
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <RolDeJuegosPanel
            groupedDays={groupedGameDays}
            onOpenGame={handleOpenRolGame}
            totalGames={totalRolGames}
          />
        )}

      {isFetchingEvents && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Filter className="h-4 w-4 animate-spin" />
          Actualizando calendario...
        </p>
      )}
      {displayMode === 'calendar' && previewEvent && (
        <EventPreviewPanel
          event={previewEvent}
          onClose={() => setPreviewEvent(null)}
          onAttendance={navigateToEvent}
          onViewGroup={handleViewGroup}
        />
      )}
    </div>
  );

  if (embedded) {
    return calendarContent;
  }

  return (
    <FeaturedLayout title={titleOverride} subtitle={subtitleOverride}>
      {calendarContent}
    </FeaturedLayout>
  );
};

const FiltersPanel = ({
  groups,
  selectedGroupIds,
  onGroupChange,
  availableTypes,
  selectedTypes,
  onTypesChange,
  showCancelled,
  onToggleCancelled,
  typesLocked = false,
  showTypesFilter = true,
  extraFilters = null
}) => {
  const toggleGroup = (groupId) => {
    const alreadySelected = selectedGroupIds.includes(groupId);
    if (alreadySelected) {
      onGroupChange(selectedGroupIds.filter((id) => id !== groupId));
    } else {
      onGroupChange([...selectedGroupIds, groupId]);
    }
  };

  const toggleType = (type) => {
    const exists = selectedTypes.includes(type);
    if (exists) {
      onTypesChange(selectedTypes.filter((t) => t !== type));
    } else {
      onTypesChange([...selectedTypes, type]);
    }
  };

  return (
    <Card className="border shadow-none bg-muted/30">
      <CardContent className="p-4 space-y-4">
        {extraFilters && (
          <div className="border-b pb-4">
            {extraFilters}
          </div>
        )}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Grupos</p>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <button type="button" onClick={() => onGroupChange([])} className="hover:text-foreground">
                Limpiar
              </button>
              <span className="text-muted-foreground/40">|</span>
              <button
                type="button"
                onClick={() => onGroupChange(groups.map((group) => group.id))}
                className="text-primary hover:underline"
              >
                Todos
              </button>
            </div>
          </div>
          {groups.length === 0 ? (
            <p className="text-xs text-muted-foreground">No tienes grupos asignados.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {groups.map((group) => {
                const active = selectedGroupIds.includes(group.id);
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs transition',
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'text-muted-foreground border-muted-foreground/50 hover:border-foreground'
                    )}
                  >
                    {group.nombre_del_grupo}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!typesLocked && showTypesFilter && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Tipos de evento</p>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <button type="button" onClick={() => onTypesChange([])} className="hover:text-foreground">
                  Limpiar
                </button>
                <span className="text-muted-foreground/40">|</span>
                <button
                  type="button"
                  onClick={() => onTypesChange(availableTypes)}
                  className="text-primary hover:underline"
                >
                  Todos
                </button>
              </div>
            </div>
            {availableTypes.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay eventos en el rango seleccionado.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableTypes.map((type) => {
                  const active = selectedTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleType(type)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs transition',
                        active
                          ? 'bg-secondary text-secondary-foreground border-secondary'
                          : 'text-muted-foreground border-muted-foreground/50 hover:border-foreground'
                      )}
                    >
                      {formatEventType(type)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Cancelados</p>
            <p className="text-[11px] text-muted-foreground">Incluye eventos con estatus cancelado</p>
          </div>
          <Button
            type="button"
            variant={showCancelled ? 'secondary' : 'ghost'}
            size="sm"
            className="text-xs px-2 py-1 flex items-center gap-1"
            onClick={onToggleCancelled}
          >
            <XCircle className="h-3 w-3" />
            {showCancelled ? 'Ocultar' : 'Ver cancelados'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SummaryCard = ({ label, value, tone }) => {
  const toneClasses = {
    info: 'bg-blue-50 border-blue-100 text-blue-900',
    success: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    warning: 'bg-amber-50 border-amber-100 text-amber-900',
    muted: 'bg-slate-50 border-slate-200 text-slate-900'
  };

  return (
    <Card className={cn('border shadow-none', toneClasses[tone] || toneClasses.muted)}>
      <CardContent className="py-1.5 px-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground/80">{label}</p>
        <p className="text-lg font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
};

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-1 text-sm text-muted-foreground">
    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </div>
);

const RolDeJuegosPanel = ({ groupedDays, onOpenGame, totalGames }) => {
  if (groupedDays.length === 0) {
    return (
      <Card className="border-dashed border-2 text-center py-12">
        <CardContent className="space-y-2">
          <Trophy className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="text-base font-semibold">No hay juegos programados</p>
          <p className="text-sm text-muted-foreground">
            Cuando se registren juegos para los grupos seleccionados, verás el rol agrupado aquí.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="secondary" className="flex items-center gap-1">
          <Trophy className="h-3 w-3" />
          {groupedDays.length} días
        </Badge>
        <Badge variant="outline" className="flex items-center gap-1">
          <Clock3 className="h-3 w-3" />
          {totalGames} juegos
        </Badge>
      </div>
      {groupedDays.map((day) => (
        <Card key={day.dateKey} className="border shadow-sm">
          <CardContent className="space-y-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase text-muted-foreground">Rol del día</p>
                <h3 className="text-lg font-semibold capitalize">{day.label}</h3>
              </div>
              <Badge variant="outline">{day.torneos.length} torneos</Badge>
            </div>

            <div className="space-y-4">
              {day.torneos.map((torneo) => (
                <div key={`${day.dateKey}-${torneo.torneoId}`} className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-primary" />
                      {torneo.torneoName}
                    </p>
                    <Badge variant="secondary">
                      {torneo.campos.reduce((acc, campo) => acc + campo.juegos.length, 0)} juegos
                    </Badge>
                  </div>
                  <div
                    className="grid gap-3 grid-cols-1 md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
                    style={{ '--cols': Math.max(torneo.campos.length, 1) }}
                  >
                    {torneo.campos.length === 0 ? (
                      <div className="text-sm text-muted-foreground border rounded-md bg-white/40 px-4 py-3">
                        No hay juegos registrados para este torneo en la fecha seleccionada.
                      </div>
                    ) : (
                      torneo.campos.map((campo) => (
                        <div
                          key={`${day.dateKey}-${torneo.torneoId}-${campo.campoKey}`}
                          className="rounded-lg border bg-white shadow-sm"
                        >
                          <div className="flex items-center justify-between px-4 py-2 border-b">
                            <p className="text-sm font-semibold">{campo.campoLabel}</p>
                            <Badge variant="outline">{campo.juegos.length}</Badge>
                          </div>
                          <div className="divide-y">
                            {campo.juegos.map((juego) => (
                              <div
                                key={juego.resource?.juegoId}
                                className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div>
                                  <p className="text-sm font-medium flex items-center gap-2">
                                    <Clock3 className="h-4 w-4 text-muted-foreground" />
                                    {format(juego.start, 'HH:mm')} · {juego.title}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Grupo {juego.resource?.groupName || 'Sin grupo'} · Estatus:{' '}
                                    {formatEventType(juego.resource?.status)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {juego.resource?.pendingAttendance && (
                                    <Badge
                                      variant="outline"
                                      className="text-[11px] border-amber-300 text-amber-700 bg-amber-50"
                                    >
                                      Pendiente
                                    </Badge>
                                  )}
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    className="flex items-center gap-1"
                                    onClick={() => onOpenGame(juego)}
                                  >
                                    <Play className="h-3 w-3" />
                                    Ver juego
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const AgendaList = ({ events, rangeStart, rangeEnd, onSelectEvent, onOpenGame }) => {
  if (!events.length) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        No hay eventos programados en este periodo.
      </div>
    );
  }

  const rangeLabel = `${format(rangeStart, "dd MMM yyyy", { locale: es })} - ${format(rangeEnd, "dd MMM yyyy", {
    locale: es
  })}`;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 text-xs text-muted-foreground">
        <span>{rangeLabel}</span>
        <span>{events.length} registros en la agenda</span>
      </div>
      <div className="space-y-3 md:hidden px-3 pb-3">
        {events.map((event) => {
          const timeLabel = `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`;
          const groupName = event.resource?.groupName || 'Sin grupo';
          const torneoName =
            event.originalEvent?.torneo?.nombre ||
            event.originalEvent?.torneo_nombre ||
            event.originalEvent?.torneoName ||
            null;

          return (
            <div key={`${event.resource?.eventoId}-${event.start.toISOString()}`} className="rounded-lg border bg-white p-3 space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{format(event.start, "eee dd MMM", { locale: es })}</span>
                <span>{timeLabel}</span>
              </div>
              <button
                className="text-left font-semibold text-primary hover:underline"
                onClick={() => onSelectEvent(event)}
              >
                {event.originalEvent?.nombre || event.title}
              </button>
              <p className="text-xs text-muted-foreground">
                {groupName}
                {event.resource?.tipo === 'juego' && event.originalEvent?.equipo?.nombre
                  ? ` vs ${event.originalEvent?.equipo?.nombre}`
                  : ''}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="rounded-full border px-2 py-0.5">
                  {formatEventType(event.resource?.tipo)}
                </span>
                {torneoName && <span className="rounded-full border px-2 py-0.5">{torneoName}</span>}
                {event.resource?.mundo === MUNDO_RED && (
                  <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-blue-700">MYKLUBB Torneos</span>
                )}
                {event.resource?.pendingAttendance && (
                  <span className="text-amber-600 font-medium">Pendiente de asistencia</span>
                )}
              </div>
              {onOpenGame && (
                <Button size="sm" variant="outline" className="flex items-center gap-2" onClick={() => onOpenGame(event)}>
                  <Play className="h-4 w-4" />
                  {event.resource?.tipo === 'juego' ? 'Ver juego' : 'Tomar asistencia'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Fecha</th>
              <th className="px-4 py-2 text-left font-semibold">Hora</th>
              <th className="px-4 py-2 text-left font-semibold">Detalle</th>
              <th className="px-4 py-2 text-left font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => {
              const timeLabel = `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`;
              const groupName = event.resource?.groupName || 'Sin grupo';
              const torneoName =
                event.originalEvent?.torneo?.nombre ||
                event.originalEvent?.torneo_nombre ||
                event.originalEvent?.torneoName ||
                null;

              return (
                <tr key={`${event.resource?.eventoId}-${event.start.toISOString()}`} className="border-b last:border-b-0">
                  <td className="px-4 py-3 whitespace-nowrap font-medium">
                    {format(event.start, "eee dd MMM", { locale: es })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{timeLabel}</td>
                  <td className="px-4 py-3">
                    <button
                      className="text-left font-semibold text-primary hover:underline"
                      onClick={() => onSelectEvent(event)}
                    >
                      {event.originalEvent?.nombre || event.title}
                    </button>
                    <p className="text-xs text-muted-foreground mt-1">
                      {groupName}
                      {event.resource?.tipo === 'juego' && event.originalEvent?.equipo?.nombre
                        ? ` vs ${event.originalEvent?.equipo?.nombre}`
                        : ''}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded-full border px-2 py-0.5">
                        {formatEventType(event.resource?.tipo)}
                      </span>
                      {torneoName && <span className="rounded-full border px-2 py-0.5">{torneoName}</span>}
                      {event.resource?.mundo === MUNDO_RED && (
                        <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-blue-700">MYKLUBB Torneos</span>
                      )}
                {event.resource?.mundo === MUNDO_RED && (
                  <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-blue-700">MYKLUBB Torneos</span>
                )}
                      {event.resource?.pendingAttendance && (
                        <span className="text-amber-600 font-medium">Pendiente de asistencia</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {onOpenGame && (
                      <Button size="xs" variant="outline" className="flex items-center gap-1" onClick={() => onOpenGame(event)}>
                        <Play className="h-3 w-3" />
                        {event.resource?.tipo === 'juego' ? 'Ver juego' : 'Tomar asistencia'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CalendarToolbar = ({ currentDate, view, onNavigate, onViewChange, isMobile }) => {
  const label = getToolbarLabel(view, currentDate);
  const viewOptions = isMobile
    ? [{ key: 'day', label: 'Día' }]
    : [
        { key: 'day', label: 'Día' },
        { key: 'week', label: 'Semana' }
      ];

  return (
    <div className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => onNavigate('TODAY')}>
          <CalendarIcon className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onNavigate('PREV')}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onNavigate('NEXT')}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <h3 className="text-base font-semibold capitalize">{label}</h3>
      </div>
      <div className="flex items-center gap-2">
        {viewOptions.map((option) => (
          <Button
            key={option.key}
            size="sm"
            variant={view === option.key ? 'default' : 'ghost'}
            onClick={() => onViewChange(option.key)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const CalendarEventCard = ({ event }) => {
  const groupName = event.resource?.groupName || 'Sin grupo';
  const clusterIndex = event.resource?.clusterIndex || 1;
  const clusterTotal = event.resource?.clusterTotal || 1;
  const shortGroup = groupName.length > 12 ? `${groupName.slice(0, 11)}...` : groupName;

  return (
    <div className="flex h-full flex-col items-center justify-center text-white text-base font-semibold leading-tight">
      {clusterTotal > 1 && (
        <span className="mb-1 text-[10px] uppercase tracking-widest opacity-80">
          {clusterIndex}/{clusterTotal}
        </span>
      )}
      <span className="text-lg">{shortGroup}</span>
    </div>
  );
};

const EventPreviewPanel = ({ event, onClose, onAttendance, onViewGroup }) => {
  const tipo = event.resource?.tipo || 'evento';
  const groupName = event.resource?.groupName || 'Sin grupo';
  const torneoName =
    event.originalEvent?.torneo?.nombre ||
    event.originalEvent?.torneo_nombre ||
    null;
  const locationLabel =
    event.resource?.canchaLabel ||
    event.originalEvent?.ubicacion ||
    event.originalEvent?.serie_entrenamiento?.nombre ||
    '';
  const timeLabel = `${format(event.start, 'EEEE d MMMM', { locale: es })} · ${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`;

  return (
    <div className="fixed inset-x-3 bottom-3 z-40 w-auto rounded-2xl border bg-white shadow-2xl md:inset-auto md:right-6 md:bottom-6 md:w-full md:max-w-md">
      <div className="flex items-start justify-between border-b px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {tipo === 'juego' ? 'Juego' : 'Entrenamiento'}
            {tipo === 'juego' && event.resource?.mundo === MUNDO_RED ? ' · MYKLUBB Torneos' : ''}
          </p>
          <h3 className="text-lg font-semibold leading-tight">{event.title}</h3>
          <p className="text-sm text-muted-foreground">{timeLabel}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <XCircle className="h-5 w-5" />
        </Button>
      </div>
      <div className="space-y-2 px-4 py-3 text-sm">
        <p>
          <span className="font-semibold">Grupo:</span> {groupName}
        </p>
        {torneoName && (
          <p>
            <span className="font-semibold">Torneo:</span> {torneoName}
          </p>
        )}
        {locationLabel && (
          <p>
            <span className="font-semibold">Lugar:</span> {locationLabel}
          </p>
        )}
        <p>
          <span className="font-semibold">Estado:</span> {formatEventType(event.resource?.status)}
          {event.resource?.pendingAttendance ? ' · Pendiente de asistencia' : ''}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
        {event.resource?.grupoId && (
          <Button variant="outline" onClick={() => onViewGroup(event)}>
            Ver grupo
          </Button>
        )}
          <Button onClick={() => onAttendance(event)} className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            {event.resource?.tipo === 'juego' ? 'Ver juego' : 'Tomar asistencia'}
          </Button>
      </div>
    </div>
  );
};

const getToolbarLabel = (currentView, referenceDate) => {
  if (currentView === 'day') {
    return format(referenceDate, "EEEE d 'de' MMMM", { locale: es });
  }

  if (currentView === 'week') {
    const start = startOfWeek(referenceDate, { weekStartsOn: 1 });
    const end = addDays(start, 6);
    return `${format(start, "d MMM", { locale: es })} - ${format(end, "d MMM yyyy", { locale: es })}`;
  }

  const startAgenda = startOfDay(referenceDate);
  const endAgenda = addDays(startAgenda, 30);
  return `${format(startAgenda, "dd MMM", { locale: es })} - ${format(endAgenda, "dd MMM yyyy", { locale: es })}`;
};

const formatEventType = (type = '') => {
  if (!type) return 'Evento';
  return type
    .split(/[\s_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getFilterCount = ({
  groups,
  selectedGroupIds,
  availableTypes,
  selectedTypes,
  showCancelled,
  typesLocked,
  showTypes = true,
  extraFilterCount = 0
}) => {
  const hasGroups = groups.length > 0;
  const hasTypes = availableTypes.length > 0;
  const groupActive =
    hasGroups &&
    (selectedGroupIds.length === 0 || selectedGroupIds.length !== groups.length);
  const typesActive =
    hasTypes &&
    (selectedTypes.length === 0 || selectedTypes.length !== availableTypes.length);

  const typesVisible = showTypes && !typesLocked;
  return [groupActive, typesVisible ? typesActive : false, showCancelled]
    .filter(Boolean)
    .length + (extraFilterCount || 0);
};

export default CalendarOverview;









